import React, { useState, useEffect, useCallback } from 'react';
import request from '../utils/request';

export default function MarketManagementScreen() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({ slug: '', name: '', state: '', lat: '', lng: '', radiusMiles: '30', timezone: 'America/New_York' });

  const fetchMarkets = useCallback(async () => {
    try {
      const { data } = await request.get('/admin/markets');
      setMarkets(data.markets || []);
    } catch (err) {
      console.error('Failed to fetch markets:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMarkets(); }, [fetchMarkets]);

  const resetForm = () => {
    setForm({ slug: '', name: '', state: '', lat: '', lng: '', radiusMiles: '30', timezone: 'America/New_York' });
    setShowCreate(false);
    setEditingId(null);
  };

  const handleCreate = async () => {
    try {
      await request.post('/admin/markets', {
        slug: form.slug,
        name: form.name,
        state: form.state,
        center: { coordinates: [parseFloat(form.lng), parseFloat(form.lat)] },
        radiusMiles: parseInt(form.radiusMiles, 10),
        timezone: form.timezone,
      });
      resetForm();
      fetchMarkets();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleUpdate = async () => {
    try {
      const body = {
        name: form.name,
        state: form.state,
        radiusMiles: parseInt(form.radiusMiles, 10),
        timezone: form.timezone,
      };
      if (form.lat && form.lng) {
        body.center = { type: 'Point', coordinates: [parseFloat(form.lng), parseFloat(form.lat)] };
      }
      await request.patch(`/admin/markets/${editingId}`, body);
      resetForm();
      fetchMarkets();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleToggleActive = async (market) => {
    try {
      await request.patch(`/admin/markets/${market._id}`, { isActive: !market.isActive });
      fetchMarkets();
    } catch (err) {
      alert('Failed to toggle market');
    }
  };

  const startEdit = (market) => {
    setForm({
      slug: market.slug,
      name: market.name,
      state: market.state || '',
      lat: String(market.center?.coordinates?.[1] || ''),
      lng: String(market.center?.coordinates?.[0] || ''),
      radiusMiles: String(market.radiusMiles),
      timezone: market.timezone || 'America/New_York',
    });
    setEditingId(market._id);
    setShowCreate(true);
  };

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Market Management</h1>
            <p className="text-sm text-zinc-500 mt-1">{markets.length} market{markets.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowCreate(true); }}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
          >
            + Add Market
          </button>
        </div>

        {showCreate && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-4">
              {editingId ? 'Edit Market' : 'New Market'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {!editingId && (
                <Input label="Slug" value={form.slug} onChange={(v) => setForm((f) => ({ ...f, slug: v }))} placeholder="e.g., los-angeles" />
              )}
              <Input label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Los Angeles" />
              <Input label="State" value={form.state} onChange={(v) => setForm((f) => ({ ...f, state: v }))} placeholder="CA" />
              <Input label="Latitude" value={form.lat} onChange={(v) => setForm((f) => ({ ...f, lat: v }))} placeholder="34.0522" />
              <Input label="Longitude" value={form.lng} onChange={(v) => setForm((f) => ({ ...f, lng: v }))} placeholder="-118.2437" />
              <Input label="Radius (miles)" value={form.radiusMiles} onChange={(v) => setForm((f) => ({ ...f, radiusMiles: v }))} placeholder="30" />
              <Input label="Timezone" value={form.timezone} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} placeholder="America/Los_Angeles" />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={editingId ? handleUpdate : handleCreate}
                className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700"
              >
                {editingId ? 'Save Changes' : 'Create Market'}
              </button>
              <button onClick={resetForm} className="px-5 py-2.5 bg-zinc-100 text-zinc-600 text-sm font-medium rounded-xl hover:bg-zinc-200">
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Market</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">State</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Radius</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Providers</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {markets.map((m) => (
                  <tr key={m._id} className="hover:bg-zinc-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-zinc-900">{m.name}</div>
                      <div className="text-xs text-zinc-400">{m.slug}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-600">{m.state || '\u2014'}</td>
                    <td className="px-6 py-4 text-sm text-zinc-600">{m.radiusMiles} mi</td>
                    <td className="px-6 py-4 text-sm text-zinc-600">{m.providerCount || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                        m.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(m)} className="text-sm text-indigo-600 hover:text-indigo-800">Edit</button>
                        <button
                          onClick={() => handleToggleActive(m)}
                          className={`text-sm ${m.isActive ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}
                        >
                          {m.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
      />
    </div>
  );
}
